import { ImgHTMLAttributes, useMemo } from 'react';
import { resolutions } from './resolutions';

interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  resolution: string;
  src: string;
  alt?: string;
  sizes?: string;
  image_link?: Record<string, string>;
}

const bucketUrl =
  window.location.hostname === 'cybershtab.ru'
    ? 'https://media.cybershtab.ru/cybershtab-front/'
    : 'http://10.244.0.242:9000/cybershtab-local-bucket/'

const createPath = (path: string, resolution?: string) => {
  if (!path || path.startsWith('../') || path.startsWith('http')) {
    return path
  }

  if (!resolution)
    return `${bucketUrl}${path}`

  const lastSlashIndex = path.lastIndexOf('/');

  if (lastSlashIndex === -1)
    return `${bucketUrl}${resolution}/${path}`;

  return `${bucketUrl}${path.slice(0, lastSlashIndex)}/${resolution}${path.slice(lastSlashIndex)}`;
}

const createSrcSet = (src: string, resolutionArray: string[]) =>
  resolutionArray.map((res) => {
    const width = res.split('x')[0]
    return `${createPath(src, res)} ${width}w`
  }).join(', ')

const parsedLink = (link: Record<string, string>) => {
  let original: string | undefined

  const parsed = Object.entries(link)
    .map(([key, value]) => {
      if (key === 'original') {
        original = value
        return null
      }

      const width = Number(key.split('x')[0])

      if (Number.isNaN(width)) return null

      return {
        width,
        src: createPath(value),
      }
    })
    .filter((item): item is { width: number; src: string } => item !== null)
    .sort((a, b) => a.width - b.width)

  const srcSet = parsed.length
    ? parsed.map(item => `${item.src} ${item.width}w`).join(', ')
    : undefined

  return {
    currentSrc: original ? createPath(original, '') : '',
    srcSet
  }
}

export default function Image({ src, alt = '', sizes, resolution, image_link, ...props }: ImageProps) {
  const { currentSrc, srcSet } = useMemo(() => {
    if (src.startsWith('../')) {
      return {
        currentSrc: src.slice(2),
        srcSet: undefined,
      }
    }

    if (src.startsWith('http')) {
      return {
        currentSrc: src,
        srcSet: undefined,
      }
    }

    if (image_link) {
      return parsedLink(image_link)
    }

    const resolutionArray = resolutions[resolution as keyof typeof resolutions]

    if (!resolutionArray || resolutionArray.length === 0) {
      return {
        currentSrc: createPath(src),
        srcSet: undefined,
      }
    }

    return {
      currentSrc: createPath(src),
      srcSet: createSrcSet(src, resolutionArray),
    }
  }, [src, resolution, image_link])

  if (!currentSrc) return null

  return (
    <img
      src={currentSrc}
      alt={alt}
      sizes={sizes}
      srcSet={srcSet}
      {...props}
    />
  )
}